import type { MouseEventHandler } from "react";
import { useRef } from "react";
import { cx } from "class-variance-authority";
import { CSSTransition } from "react-transition-group";
import { usePlaybackStore } from "~/stores/playerStore";
import { ActionButton } from "./ActionButton";
import { PauseToggleButton } from "./PauseToggleButton";
import { SeekingControl } from "./SeekingControl";
import styles from "./index.module.css";

interface ControlsProps {
  controlsVisibleTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  visible: boolean;
  onDefaultScreenClick: () => void;
  onPauseToggle?: MouseEventHandler;
  onSeekByProgress: (nextProgress: number) => void;
  onPlay: () => void;
  onPause: () => void;
  liked: boolean;
  likesCount: string;
  onLike: () => void;
  onShare: (e: any) => Promise<void>;
}

export function Controls({
  controlsVisibleTimeoutRef,
  visible,
  onDefaultScreenClick,
  onPauseToggle,
  onSeekByProgress,
  onPlay,
  onPause,
  liked,
  likesCount,
  onLike,
  onShare,
}: ControlsProps) {
  const nodeRef = useRef(null);

  function restartControlsVisibleTimeout() {
    if (controlsVisibleTimeoutRef.current) {
      clearTimeout(controlsVisibleTimeoutRef.current);
    }

    controlsVisibleTimeoutRef.current = setTimeout(() => {
      onDefaultScreenClick();
    }, 3000);
  }

  function onDragStart() {
    if (controlsVisibleTimeoutRef.current) {
      clearTimeout(controlsVisibleTimeoutRef.current);
    }
  }
  const paused = usePlaybackStore(state => state.paused);

  function onDragEnd() {
    restartControlsVisibleTimeout();
  }

  return (
    <CSSTransition
      classNames="controls"
      in={visible}
      nodeRef={nodeRef}
      timeout={300}
    >
      <div
        ref={nodeRef}
        className={cx(styles.container, visible && styles.visible)}
        // onMouseUp={onDefaultScreenClick}
      >
        <div className={styles.topDim} />
        <div className={styles.wrapper}>
          {/* @ts-ignore */}
          <PauseToggleButton paused={paused} onClick={onPauseToggle} />
          <div className={styles.actionButtonBar}>
            <ActionButton
              likesCount={likesCount}
              status={liked ? "active" : undefined}
              type="like"
              onClick={e => {
                e.stopPropagation();
                onLike();
                restartControlsVisibleTimeout();
              }}
            />
            <ActionButton
              type="share"
              onClick={e => {
                e.stopPropagation();
                onShare(e);
                restartControlsVisibleTimeout();
              }}
            />
          </div>
          <SeekingControl
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onPause={onPause}
            onPlay={onPlay}
            onSeekByProgress={onSeekByProgress}
          />
        </div>
        <div className={styles.bottomDim} />
      </div>
    </CSSTransition>
  );
}
